import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Hero } from "@/components/sections/Hero";
import { Reveal } from "@/components/ui/Reveal";
import { RichText } from "@/components/ui/RichText";
import { ServiceBlock } from "@/components/sections/ServiceBlocks";
import { Seo } from "@/components/ui/Seo";
import { ErrorBanner, CardSkeleton } from "@/components/ui/States";
import { useFetch } from "@/hooks/useFetch";
import { localizedPath, pick } from "@/lib/helpers";
import { mediaUrl } from "@/lib/api";

// Turn "secure-communications" → "Secure Communications" so the hero never feels empty.
function slugToTitle(slug) {
  if (!slug) return "";
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ServiceDetail() {
  const { lang = "en", slug } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: s, loading, error, refetch } = useFetch(`/services/by-slug/${slug}`, [slug]);

  // handle slug redirect
  useEffect(() => {
    if (s && s.redirect) {
      navigate(localizedPath(lang, `/services/${s.redirect}`), { replace: true });
    }
  }, [s, lang, navigate]);

  // True 404 only when the request finished successfully but returned nothing
  // (or a typed 404). A transient network/server error is recoverable via retry,
  // so we never trap the user on a fake "not found" screen.
  const status = error?.response?.status;
  const isHardNotFound = !loading && !error && !s;
  const isNotFoundResponse = status === 404;

  if (isHardNotFound || isNotFoundResponse) {
    return (
      <div className="container-x py-32 text-center" data-testid="service-not-found">
        <Seo title={t("services.notFoundTitle")} lang={lang} path={localizedPath(lang, "/services")} />
        <p className="eyebrow mb-3">404</p>
        <h1 className="h-display text-fg text-4xl md:text-5xl">{t("services.notFoundTitle")}</h1>
        <p className="mt-4 text-mut max-w-md mx-auto">{t("services.notFoundText")}</p>
        <div className="mt-8">
          <Link to={localizedPath(lang, "/services")} className="btn-primary" data-testid="service-back-link">
            {t("cta.backToServices")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  // Render-once placeholders so hero is never empty during loading.
  const placeholderTitle = slugToTitle(slug);
  const blocks = (s?.contentSections || []).filter((b) => b.visible !== false).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const related = s?.relatedServices || [];

  return (
    <>
      <Seo
        title={pick({ en: s?.seoTitleEn || s?.titleEn, fr: s?.seoTitleFr || s?.titleFr }, lang) || placeholderTitle}
        description={pick({ en: s?.seoDescriptionEn || s?.summaryEn, fr: s?.seoDescriptionFr || s?.summaryFr }, lang)}
        lang={lang}
        path={localizedPath(lang, `/services/${slug}`)}
        image={mediaUrl(s?.socialSharingImage || s?.heroImage)}
      />

      {/* A. Service Hero — uses slug placeholder while data is in-flight */}
      <Hero
        eyebrow={pick({ en: s?.eyebrowEn, fr: s?.eyebrowFr }, lang)}
        title={pick({ en: s?.titleEn, fr: s?.titleFr }, lang) || placeholderTitle}
        subtitle={pick({ en: s?.summaryEn, fr: s?.summaryFr }, lang)}
        image={s?.heroImage}
      >
        <Link to={localizedPath(lang, s?.ctaDestination || "/contact")} className="btn-primary" data-testid="service-hero-cta">
          {pick({ en: s?.ctaLabelEn, fr: s?.ctaLabelFr }, lang) || t("cta.talkExpert")} <ArrowRight className="w-4 h-4" />
        </Link>
      </Hero>

      {/* Breadcrumb (always renders) */}
      <div className="container-x pt-6">
        <nav className="flex items-center gap-2 font-techmono text-xs uppercase tracking-wider text-mut" aria-label="Breadcrumb">
          <Link to={localizedPath(lang, "/")} className="hover:text-accent">{t("nav.home")}</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={localizedPath(lang, "/services")} className="hover:text-accent">{t("nav.services")}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-fg">{pick({ en: s?.titleEn, fr: s?.titleFr }, lang) || placeholderTitle}</span>
        </nav>
      </div>

      {/* Inline retry banner if the fetch failed (e.g. network blip), instead of a fake 404. */}
      {error && !isNotFoundResponse && !s ? (
        <ErrorBanner onRetry={refetch} testId="service-detail-error" />
      ) : null}

      {/* Skeleton placeholders while real content is in-flight (after slug placeholder hero) */}
      {loading && !s ? (
        <section className="container-x py-16 md:py-20" data-testid="service-detail-skeleton">
          <div className="max-w-3xl space-y-4">
            <div className="h-4 w-1/3 bg-white/5 animate-pulse" />
            <div className="h-6 w-3/4 bg-white/5 animate-pulse" />
            <div className="h-4 w-full bg-white/5 animate-pulse" />
            <div className="h-4 w-5/6 bg-white/5 animate-pulse" />
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <CardSkeleton key={i} />)}
          </div>
        </section>
      ) : null}

      {/* B. Introduction */}
      {s && (s.introductionEn || s.introductionFr || s.taglineEn || s.taglineFr) && (
        <section className="container-x py-16 md:py-20" data-testid="service-introduction">
          <div className="max-w-3xl">
            {(s.taglineEn || s.taglineFr) ? <div className="eyebrow mb-3">{pick({ en: s.taglineEn, fr: s.taglineFr }, lang)}</div> : null}
            <RichText html={pick({ en: s.introductionEn, fr: s.introductionFr }, lang)} className="text-lg" />
          </div>
        </section>
      )}

      {/* C–H. Content blocks */}
      {s && blocks.map((block, i) => (
        <section key={block.id || i} className={`${i % 2 === 1 ? "bg-surface border-y border-hair" : ""} py-16 md:py-20`} data-testid={`service-block-${block.type}`}>
          <div className="container-x">
            <Reveal>
              <ServiceBlock block={block} related={related} />
            </Reveal>
          </div>
        </section>
      ))}

      {/* H. Related services */}
      {s && related.length > 0 && !blocks.some((b) => b.type === "relatedServices") && (
        <section className="bg-surface border-y border-hair py-16 md:py-20" data-testid="service-related">
          <div className="container-x">
            <ServiceBlock block={{ type: "relatedServices", visible: true }} related={related} />
          </div>
        </section>
      )}

      {/* I. Contact CTA */}
      {s ? (
        <section className="relative overflow-hidden" data-testid="service-cta">
          <img src={mediaUrl(s.heroImage, { w: 1600, fmt: "webp", q: 75 })} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink-900/85" />
          <div className="absolute inset-0 tech-grid opacity-40" />
          <div className="absolute top-0 left-0 w-full h-px bg-cyan/60" />
          <div className="container-x relative py-20 md:py-28 text-center">
            <h2 className="h-display text-cloud text-[clamp(2rem,4.5vw+0.5rem,3.5rem)] max-w-3xl mx-auto">
              {pick({ en: s.ctaTitleEn, fr: s.ctaTitleFr }, lang) || t("cta.getInTouch")}
            </h2>
            {(s.ctaDescriptionEn || s.ctaDescriptionFr) ? (
              <p className="mt-5 text-steel max-w-xl mx-auto text-lg">{pick({ en: s.ctaDescriptionEn, fr: s.ctaDescriptionFr }, lang)}</p>
            ) : null}
            <Link to={localizedPath(lang, s.ctaDestination || "/contact")} className="btn-primary mt-9" data-testid="service-cta-button">
              {pick({ en: s.ctaLabelEn, fr: s.ctaLabelFr }, lang) || t("cta.contactUs")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      ) : null}
    </>
  );
}
